import { useState, useEffect } from 'react';
import useStock from './hooks/useStock';
import useTheme from './hooks/useTheme';
import useLang from './hooks/useLang';
import { t } from './utils/i18n';
import AuthPage from './components/Auth/AuthPage';
import ProfileModal from './components/Auth/ProfileModal';
import { isLoggedIn, getUser, clearAuth } from './utils/auth';
import SearchBar from './components/Search/SearchBar';
import CompositeScore from './components/Score/CompositeScore';
import FundamentalsPanel from './components/Score/FundamentalsPanel';
import ValuationPanel from './components/Valuation/ValuationPanel';
import TechnicalChart from './components/Technical/TechnicalChart';
import AIInsight from './components/AIInsight/AIInsight';
import Watchlist from './components/Watchlist/Watchlist';
import PeersTable from './components/Peers/PeersTable';
import HoldingsTab from './components/ETF/HoldingsTab';
import DividendTab from './components/Dividend/DividendTab';
import MoatTab from './components/Moat/MoatTab';
import { MoatBadge, KeyMetricsCard } from './components/Overview/OverviewExtras';
import TrendCharts from './components/Overview/TrendCharts';
import TradingTab from './components/Trading/TradingTab';
import SetAlertModal from './components/Alerts/SetAlertModal';
import AlertInbox, { BellButton } from './components/Alerts/AlertInbox';
import useLivePrice from './hooks/useLivePrice';
import { fmt, colorByValue } from './utils/format';
import useWatchlist from './hooks/useWatchlist';
import LandingPage from './components/Landing/LandingPage';
import PortfolioPage from './components/Portfolio/PortfolioPage';
import NewsTab from './components/News/NewsTab';
import axios from 'axios';
import { getToken } from './utils/auth';

const EQUITY_TAB_KEYS  = ['overview','fundamentals','valuation','technical','trading','dividend','moat','aiInsight','peers','news'];
const ETF_TAB_KEYS     = ['technical','trading','holdings','dividend','peers'];
const CRYPTO_TAB_KEYS  = ['technical','trading','aiInsight','news'];
const isCryptoSymbol   = (sym) => /[-/](USD|USDT|USDC|BTC|ETH|BNB)$/i.test(sym || '');

function useSymbolAlerts(symbol) {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    if (!symbol) return;
    const token = getToken();
    if (!token) return;
    axios.create({ baseURL: '/api', timeout: 8000 })
      .get('/alerts', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAlerts((r.data || []).filter(a => a.symbol === symbol && a.status === 'active')))
      .catch(() => {});
  }, [symbol]);
  return { alerts, setAlerts };
}

function SnapshotHeader({ data, onWatchlistToggle, inWatchlist, onGoToMoat, onSetAlert, onBack }) {
  const { quote } = data;
  const isETF = quote?.quoteType === 'ETF';
  const { price, change, changePercent, live, extended } = useLivePrice(
    quote?.symbol, quote?.price, quote?.change, quote?.changePercent, quote
  );
  const chgColor = colorByValue(changePercent);
  const { alerts: activeAlerts, setAlerts: setActiveAlerts } = useSymbolAlerts(quote?.symbol);

  const cancelAlert = async (id) => {
    const token = getToken();
    await axios.create({ baseURL: '/api' }).delete(`/alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setActiveAlerts(a => a.filter(x => x.id !== id));
  };

  return (
    <div className="snapshot-header">
      <div className="snapshot-top">
        <div>
          {onBack && (
            <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: '0 0 8px 0', marginBottom: 2 }}>
              <span style={{ fontSize: 14 }}>←</span> Back to Portfolio
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {quote?.image && <img src={quote.image} alt="" onError={e => e.target.style.display='none'} style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain', background: '#fff', padding: 2 }} />}
            <div className="stock-name">{quote?.name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.03em' }}>{quote?.symbol}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>· {quote?.exchange} · {quote?.sector}</span>
            {!isETF && <MoatBadge symbol={quote?.symbol} onGoToMoat={onGoToMoat} />}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div className="price-block">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="price-main">{fmt.currency(price)}</div>
              {live && (
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', background: 'var(--green-dim)', border: '1px solid var(--green)', borderRadius: 4, padding: '2px 6px', letterSpacing: '0.06em' }}>LIVE</span>
              )}
            </div>
            <div className="price-change" style={{ color: chgColor }}>
              {change >= 0 ? '+' : ''}{fmt.currency(change)} ({fmt.change(changePercent)})
            </div>
            {extended && (() => {
              const extColor = extended.change >= 0 ? 'var(--green)' : 'var(--red)';
              const label = extended.type === 'pre' ? 'Pre-market' : 'After-hours';
              return (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{label}</span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 500 }}>{fmt.currency(extended.price)}</span>
                  <span style={{ color: extColor, marginLeft: 6 }}>
                    {extended.change >= 0 ? '+' : ''}{fmt.currency(extended.change)} ({fmt.change(extended.changePercent)})
                  </span>
                </div>
              );
            })()}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => onSetAlert(price)} style={{ fontSize: 12 }}>🔔 Alert</button>
            <button className="btn btn-ghost" onClick={onWatchlistToggle} style={{ fontSize: 12 }}>
              {inWatchlist ? '★ Remove' : '☆ Watchlist'}
            </button>
          </div>
          {/* Active alerts for this symbol */}
          {activeAlerts.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', marginTop: 2 }}>
              {activeAlerts.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: a.condition === 'above' ? 'var(--green-dim)' : 'var(--red-dim)',
                  border: `1px solid ${a.condition === 'above' ? 'var(--green)' : 'var(--red)'}`,
                  borderRadius: 6, padding: '3px 8px 3px 10px',
                  fontSize: 11, fontWeight: 600,
                  color: a.condition === 'above' ? 'var(--green)' : 'var(--red)',
                }}>
                  <span>{a.condition === 'above' ? '↑' : '↓'} Alert: ${a.target_price.toFixed(2)}</span>
                  <button
                    onClick={() => cancelAlert(a.id)}
                    title="Cancel alert"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, fontSize: 11, padding: 0, lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid-4">
        <div className="stat-cell"><div className="stat-label">Market Cap</div><div className="stat-value">{fmt.large(quote?.marketCap)}</div></div>
        <div className="stat-cell"><div className="stat-label">P/E Ratio</div><div className="stat-value">{fmt.multiple(quote?.pe)}</div></div>
        <div className="stat-cell"><div className="stat-label">EPS (TTM)</div><div className="stat-value">{fmt.currency(quote?.eps)}</div></div>
        <div className="stat-cell"><div className="stat-label">52W Range</div><div className="stat-value" style={{ fontSize: 12 }}>{fmt.currency(quote?.fiftyTwoWeekLow)} – {fmt.currency(quote?.fiftyTwoWeekHigh)}</div></div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(isLoggedIn() ? getUser() : null);

  if (!user) return <AuthPage onAuth={setUser} />;

  return <MainApp user={user} onLogout={() => { clearAuth(); setUser(null); }} />;
}

function MainApp({ user: initialUser, onLogout }) {
  const { data, loading, error, currentSymbol, load, reset } = useStock();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(initialUser);
  const watchlist = useWatchlist(true);
  const [showProfile, setShowProfile] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertCurrentPrice, setAlertCurrentPrice] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [alertVersion, setAlertVersion] = useState(0);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [cameFromPortfolio, setCameFromPortfolio] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobilePage, setMobilePage] = useState('market'); // 'market' | 'watchlist' | 'portfolio' | 'search'
  const { theme, toggle: toggleTheme } = useTheme();

  // Refresh user from server to pick up is_pro flag
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.create({ baseURL: '/api', timeout: 8000 })
      .get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data?.user) {
          setUser(r.data.user);
          // Persist updated user (with is_pro) to localStorage
          localStorage.setItem('freejini_user', JSON.stringify(r.data.user));
        }
      })
      .catch(() => {});
  }, []);
  const { lang } = useLang();

  const handleSelect = (symbol) => {
    load(symbol);
    setActiveTab('overview');
    setShowPortfolio(false);
  };

  const toggleWatchlist = () => {
    if (!currentSymbol) return;
    if (watchlist.isWatched(currentSymbol)) watchlist.remove(currentSymbol);
    else watchlist.add(currentSymbol);
  };

  const inWatchlist = watchlist.isWatched(currentSymbol);

  // Fetch all active alerts (for watchlist indicators + dashboard summary)
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.create({ baseURL: '/api', timeout: 8000 })
      .get('/alerts', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setActiveAlerts(r.data || []))
      .catch(() => {});
  }, [alertVersion]);

  const alertCounts = activeAlerts.reduce((acc, a) => {
    if (a.status === 'active') acc[a.symbol] = (acc[a.symbol] || 0) + 1;
    return acc;
  }, {});

  // Poll for unread notification count + browser notifications
  useEffect(() => {
    const api = axios.create({ baseURL: '/api', timeout: 8000 });
    const poll = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const { data } = await api.get('/alerts/notifications', { headers: { Authorization: `Bearer ${token}` } });
        setUnreadAlerts(data.unread);
        // Show browser notification for new unread items
        if (data.unread > 0 && 'Notification' in window && Notification.permission === 'granted') {
          const newest = data.notifications.find(n => !n.read_at);
          if (newest && Date.now() - newest.triggered_at < 90000) {
            new Notification('Freejini Price Alert', {
              body: newest.message,
              icon: '/favicon.ico',
            });
          }
        }
      } catch {}
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="app-layout">
      {/* Mobile overlay when drawer open */}
      {mobileDrawerOpen && (
        <div className="mobile-overlay" onClick={() => setMobileDrawerOpen(false)} />
      )}

      <aside className={`sidebar${mobileDrawerOpen ? ' mobile-drawer-open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div onClick={() => { reset(); setShowPortfolio(false); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src="/freejini-icon.png" alt="Freejini" style={{ height: 38, width: 38, objectFit: 'contain', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.12em', color: '#fff', lineHeight: 1.1 }}>FREE<span style={{ color: '#22c55e' }}>JINI</span></div>
                <div style={{ fontSize: 7.5, color: 'var(--text-muted)', letterSpacing: '0.13em', marginTop: 1 }}>DATA. INSIGHTS. GROWTH.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <BellButton unread={unreadAlerts} onClick={() => setShowInbox(true)} />
              <button
                onClick={() => setShowProfile(true)}
                title="Account settings"
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--accent-dim)', border: '1.5px solid var(--accent)',
                  color: 'var(--accent)', cursor: 'pointer',
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}
              >
                {user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </button>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <SearchBar onSelect={handleSelect} placeholder={t('searchPlaceholder', lang)} />
          </div>
        </div>
        <Watchlist onSelect={handleSelect} currentSymbol={currentSymbol} alertCounts={alertCounts} watchlist={watchlist} />

        {/* Portfolio nav button */}
        <div style={{ padding: '12px 12px 4px', borderTop: '1px solid var(--border)', marginTop: 8 }}>
          <button
            onClick={() => { setShowPortfolio(true); reset(); }}
            style={{
              width: '100%', textAlign: 'left', background: showPortfolio ? 'var(--accent-dim)' : 'none',
              border: `1px solid ${showPortfolio ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: showPortfolio ? 'var(--accent)' : 'var(--text-muted)',
              display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!showPortfolio) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}}
            onMouseLeave={e => { if (!showPortfolio) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}}
          >
            <span>📂</span>
            <span>My Portfolio</span>
            {user?.is_pro && (
              <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff' }}>PRO</span>
            )}
          </button>
        </div>
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', marginTop: 'auto' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
            v{process.env.REACT_APP_GIT_HASH || 'dev'}
          </div>
        </div>
      </aside>

      <main className="main-content">
        {showPortfolio && !data && !loading && (
          <PortfolioPage user={user} onSelectStock={(sym) => { setCameFromPortfolio(true); handleSelect(sym); }} watchlist={watchlist} />
        )}

        {!showPortfolio && !data && !loading && !error && (
          <LandingPage onSelect={handleSelect} activeAlerts={activeAlerts} onAlertCancelled={() => setAlertVersion(v => v + 1)} watchlistSymbols={watchlist.symbols} />
        )}

        {loading && (
          <div style={{ padding: 24 }}>
            {[200, 120, 280, 160].map((h, i) => (
              <div key={i} className="skeleton card" style={{ height: h, marginBottom: 16 }} />
            ))}
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        {data && !loading && (
          <>
            <SnapshotHeader
              key={`${currentSymbol}-${alertVersion}`}
              data={data}
              onWatchlistToggle={toggleWatchlist}
              inWatchlist={inWatchlist}
              onGoToMoat={() => setActiveTab('moat')}
              onSetAlert={(price) => { setAlertCurrentPrice(price); setShowAlertModal(true); }}
              onBack={cameFromPortfolio ? () => { setCameFromPortfolio(false); reset(); setShowPortfolio(true); } : null}
            />
            {(() => {
              const isETF    = data.quote?.quoteType === 'ETF';
              const isCrypto = isCryptoSymbol(currentSymbol);
              const TAB_KEYS = isCrypto ? CRYPTO_TAB_KEYS : isETF ? ETF_TAB_KEYS : EQUITY_TAB_KEYS;
              const defaultTab = isCrypto || isETF ? 'technical' : 'overview';
              if (!TAB_KEYS.includes(activeTab)) setActiveTab(defaultTab);
              return (
            <div className="tab-nav">
              {TAB_KEYS.map(key => (
                <button key={key} className={`tab-btn${activeTab === key ? ' active' : ''}`} onClick={() => setActiveTab(key)}>{t(key, lang)}</button>
              ))}
            </div>
              );
            })()}
            <div className="content-area">
              {activeTab === 'overview' && (
                <>
                  <CompositeScore score={data.score} />
                  {data.quote?.quoteType !== 'ETF' && (
                    <KeyMetricsCard
                      quote={data.quote}
                      fundamentals={data.fundamentals}
                      valuation={data.valuation}
                    />
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div className="card">
                      <div className="card-title">Valuation Snapshot</div>
                      <div className="grid-2">
                        <div className="stat-cell"><div className="stat-label">Fair Value</div><div className="stat-value" style={{ color: 'var(--accent)' }}>{fmt.currency(data.valuation?.compositeFairValue)}</div></div>
                        <div className="stat-cell"><div className="stat-label">Verdict</div><div className="stat-value">{data.valuation?.verdict}</div></div>
                        <div className="stat-cell"><div className="stat-label">DCF</div><div className="stat-value">{fmt.currency(data.valuation?.dcf?.intrinsicValue)}</div></div>
                        <div className="stat-cell"><div className="stat-label">FCF Yield</div><div className="stat-value">{fmt.pct(data.valuation?.fcfYield?.yieldPct)}</div></div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-title">Technical Snapshot</div>
                      <div className="grid-2">
                        <div className="stat-cell"><div className="stat-label">Trend</div><div className="stat-value" style={{ textTransform: 'capitalize' }}>{data.technicals?.trend}</div></div>
                        <div className="stat-cell"><div className="stat-label">RSI</div><div className="stat-value">{data.technicals?.rsi?.toFixed(1)}</div></div>
                        <div className="stat-cell"><div className="stat-label">vs MA50</div><div className="stat-value" style={{ color: colorByValue(data.technicals?.priceVsMa50) }}>{fmt.pct(data.technicals?.priceVsMa50)}</div></div>
                        <div className="stat-cell"><div className="stat-label">vs MA200</div><div className="stat-value" style={{ color: colorByValue(data.technicals?.priceVsMa200) }}>{fmt.pct(data.technicals?.priceVsMa200)}</div></div>
                      </div>
                    </div>
                  </div>
                  {data.quote?.quoteType !== 'ETF' && (
                    <TrendCharts fundamentals={data.fundamentals} />
                  )}
                  {data.quote?.description && (
                    <div className="card">
                      <div className="card-title">About</div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7 }}>{data.quote.description?.slice(0, 500)}{data.quote.description?.length > 500 ? '...' : ''}</p>
                    </div>
                  )}
                </>
              )}
              {activeTab === 'fundamentals' && <FundamentalsPanel fundamentals={data.fundamentals} />}
              {activeTab === 'valuation' && <ValuationPanel valuation={data.valuation} symbol={currentSymbol} technicals={data.technicals} />}
              {activeTab === 'technical' && <TechnicalChart technicals={data.technicals} symbol={currentSymbol} />}
              {activeTab === 'holdings' && <HoldingsTab symbol={currentSymbol} />}
              {activeTab === 'dividend' && <DividendTab symbol={currentSymbol} />}
              {activeTab === 'trading' && <TradingTab symbol={currentSymbol} technicals={data.technicals} onAlertSaved={() => setAlertVersion(v => v + 1)} />}
              {activeTab === 'moat' && <MoatTab symbol={currentSymbol} />}
              {activeTab === 'aiInsight' && <AIInsight symbol={currentSymbol} />}
              {activeTab === 'peers' && <PeersTable peers={data.peers} onSelect={handleSelect} />}
              {activeTab === 'news' && <NewsTab symbol={currentSymbol} />}
            </div>
          </>
        )}
      </main>

      {showProfile && (
        <ProfileModal
          user={user}
          theme={theme}
          onThemeToggle={toggleTheme}
          onClose={() => setShowProfile(false)}
          onUserUpdate={setUser}
          onLogout={onLogout}
        />
      )}

      {showInbox && (
        <AlertInbox
          onClose={() => setShowInbox(false)}
          onUnreadChange={setUnreadAlerts}
        />
      )}

      {showAlertModal && currentSymbol && (
        <SetAlertModal
          symbol={currentSymbol}
          name={data?.quote?.name || currentSymbol}
          currentPrice={alertCurrentPrice}
          onClose={() => setShowAlertModal(false)}
          onSaved={() => setAlertVersion(v => v + 1)}
        />
      )}

      {/* ── Mobile Top Bar ── */}
      <div className="mobile-topbar">
        <button className="mobile-menu-btn" onClick={() => setMobileDrawerOpen(v => !v)}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect y="3" width="18" height="1.8" rx="0.9" fill="currentColor"/>
            <rect y="8.1" width="18" height="1.8" rx="0.9" fill="currentColor"/>
            <rect y="13.2" width="18" height="1.8" rx="0.9" fill="currentColor"/>
          </svg>
        </button>
        <div onClick={() => { reset(); setShowPortfolio(false); setMobilePage('market'); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
          <img src="/freejini-icon.png" alt="Freejini" style={{ height: 28, width: 28, objectFit: 'contain' }} />
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', color: '#fff' }}>FREE<span style={{ color: '#22c55e' }}>JINI</span></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <BellButton unread={unreadAlerts} onClick={() => setShowInbox(true)} />
          <button onClick={() => setShowProfile(true)} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent-dim)', border: '1.5px solid var(--accent)', color: 'var(--accent)', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>
            {user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
          </button>
        </div>
      </div>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="mobile-bottom-nav">
        <button className={`mobile-nav-btn${mobilePage === 'market' && !data ? ' active' : ''}`}
          onClick={() => { reset(); setShowPortfolio(false); setMobilePage('market'); setMobileDrawerOpen(false); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          <span>Market</span>
        </button>
        <button className={`mobile-nav-btn${mobilePage === 'search' ? ' active' : ''}`}
          onClick={() => { setMobilePage('search'); setMobileDrawerOpen(true); setShowPortfolio(false); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span>Search</span>
        </button>
        <button className={`mobile-nav-btn${mobilePage === 'watchlist' && !data ? ' active' : ''}`}
          onClick={() => { setMobilePage('watchlist'); setMobileDrawerOpen(true); setShowPortfolio(false); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          <span>Watchlist</span>
        </button>
        <button className={`mobile-nav-btn${mobilePage === 'portfolio' ? ' active' : ''}`}
          onClick={() => { setShowPortfolio(true); reset(); setMobilePage('portfolio'); setMobileDrawerOpen(false); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
          </svg>
          <span>Portfolio</span>
        </button>
      </nav>
    </div>
  );
}
